// HoleCameraControls.ts
import * as THREE from 'three';
import CameraControls from 'camera-controls';

const tmpV = /* @__PURE__ */ new THREE.Vector3();

function smoothFactor(dt: number, tau: number) {
  return 1 - Math.exp(-dt / tau);
}

const tau = 200;

type TrackOpts = {
  /** Which path to track against: the target path (tracker) or the camera path (curve). */
  on?: 'tracker' | 'curve';
  /** Distance metric plane. 'xz' ignores height; 'xyz' uses full 3D. Default 'xz'. */
  plane?: 'xz' | 'xyz';
  /** Coarse samples across [0,1] to get an initial bracket. */
  samples?: number;
  /** Golden-section refinement iterations (8–16 is usually enough). */
  iterations?: number;
  /** Optional local search window width around current t (e.g. 0.25). Omit to scan full [0,1]. */
  window?: number;
  /** When calling `track()`: ease the move with CameraControls transition. */
  smooth?: boolean;
  /** When calling `track()`: if true, recompute base angle at new t. */
  followBaseAngle?: boolean;
};

export class HoleCameraControls extends CameraControls {
  public curve: THREE.CatmullRomCurve3 = new THREE.CatmullRomCurve3();
  public tracker: THREE.CatmullRomCurve3 = new THREE.CatmullRomCurve3();

  public enableTravelling = true;
  public enableRotation = true;

  get isHoleCameraControls() {
    return true;
  }

  private _t = 0;
  private _angle = 0;

  public init(curve: THREE.CatmullRomCurve3, tracker: THREE.CatmullRomCurve3) {
    this.curve = curve;
    this.tracker = tracker;
    // Initialize angle to match the base angle at t=0 (tee position)
    // This ensures the camera is aligned with tee-to-pin direction
    const initialAngle = this.baseAngleAt(0);
    this.setFrom(0, initialAngle, true);
  }

  // ------------- math helpers -------------

  private clampT(t: number) {
    return THREE.MathUtils.clamp(t, 0, 1);
  }

  public pointsAt(t: number) {
    const camP = this.curve.getPointAt(this.clampT(t));
    const targetP = this.tracker.getPointAt(this.clampT(t));
    return { camP, targetP };
  }

  public baseAngleAt(t: number) {
    const { camP, targetP } = this.pointsAt(t);
    tmpV.copy(camP).sub(targetP);
    return Math.atan2(tmpV.z, tmpV.x);
  }

  public radiusAt(t: number) {
    const { camP, targetP } = this.pointsAt(t);
    tmpV.copy(camP).sub(targetP);
    return Math.hypot(tmpV.x, tmpV.z);
  }

  public connect(domElement: HTMLElement): void {
    domElement.style.setProperty('touch-action', 'none');
    super.connect(domElement);
  }

  public disconnect(): void {
    this._domElement?.style.setProperty('touch-action', 'auto');
    super.disconnect();
  }

  // ------------- core setter --------------

  public async setFrom(t: number, angle: number, enableTransition = false) {
    t = this.clampT(t);
    const { camP, targetP } = this.pointsAt(t);
    const r = this.radiusAt(t);

    const camX = targetP.x + Math.cos(angle) * r;
    const camZ = targetP.z + Math.sin(angle) * r;
    const camY = camP.y;

    // commit state
    this._t = t;
    this._angle = angle;

    await this.setLookAt(camX, camY, camZ, targetP.x, targetP.y, targetP.z, enableTransition);
  }

  public travelBy(dt: number, enableTransition?: boolean) {
    this.setFrom(this._t + dt, this._angle, enableTransition);
  }

  public async focusOn(position: THREE.Vector3, enableTransition = true) {
    await Promise.all([
      this.setLookAt(
        this.camera.position.x,
        this.camera.position.y,
        this.camera.position.z,
        position.x,
        position.y,
        position.z,
        enableTransition,
      ),
      this.dollyTo(20, enableTransition),
    ]);
  }

  private _dist2OnPlane(a: THREE.Vector3, b: THREE.Vector3, plane: 'xz' | 'xyz') {
    if (plane === 'xyz') return a.distanceToSquared(b);
    const dx = a.x - b.x,
      dz = a.z - b.z;
    return dx * dx + dz * dz;
  }

  /**
   * Find the t in [0,1] whose point on the chosen curve is closest to `pos`.
   * Uses coarse sampling + golden-section refinement; robust and fast enough for per-frame use.
   */
  public findTForPosition(pos: THREE.Vector3, opts: TrackOpts = {}): number {
    const { on = 'tracker', plane = 'xz', samples = 64, iterations = 12, window } = opts;

    const curve = on === 'tracker' ? this.tracker : this.curve;

    // Search interval
    let a = 0,
      b = 1;
    if (typeof window === 'number' && window > 0) {
      a = this.clampT(this._t - window * 0.5);
      b = this.clampT(this._t + window * 0.5);
      if (a === b) {
        a = 0;
        b = 1;
      } // degenerate fallback
    }

    // 1) Coarse scan to get a good starting bracket around the min
    let bestT = a,
      bestD2 = Infinity;
    const n = Math.max(8, samples);
    for (let i = 0; i <= n; i++) {
      const u = a + (b - a) * (i / n);
      const q = curve.getPointAt(this.clampT(u));
      const d2 = this._dist2OnPlane(q, pos, plane);
      if (d2 < bestD2) {
        bestD2 = d2;
        bestT = u;
      }
    }
    // Bracket around the best coarse sample
    const step = (b - a) / n;
    a = Math.max(0, bestT - step * 1.5);
    b = Math.min(1, bestT + step * 1.5);

    // 2) Golden-section refinement on [a,b]
    const phi = 0.6180339887498949; // 1 - 1/φ
    let c = b - phi * (b - a);
    let d = a + phi * (b - a);

    const f = (u: number) => {
      const q = curve.getPointAt(this.clampT(u));
      return this._dist2OnPlane(q, pos, plane);
    };

    let fc = f(c),
      fd = f(d);
    for (let k = 0; k < iterations; k++) {
      if (fc < fd) {
        b = d;
        d = c;
        fd = fc;
        c = b - phi * (b - a);
        fc = f(c);
      } else {
        a = c;
        c = d;
        fc = fd;
        d = a + phi * (b - a);
        fd = f(d);
      }
    }
    const tStar = this.clampT((a + b) * 0.5);
    return tStar;
  }

  /**
   * Compute best t for `pos` and (optionally) move the camera there.
   * Returns the chosen t.
   */
  public async trackOn(
    pos: THREE.Vector3,
    enableTransition: boolean,
    opts: TrackOpts = {},
  ): Promise<number> {
    const t = this.findTForPosition(pos, opts);
    const angle = opts.followBaseAngle ? this.baseAngleAt(t) : this._angle;
    await this.setFrom(t, angle, enableTransition);
    return t;
  }

  public onDrag(
    dx: number,
    dy: number,
    down: boolean,
    first: boolean,
    last: boolean,
    timeDelta: number,
    cancel: () => void,
  ) {
    let { angle, t, enableRotation, enableTravelling } = this;
    const { height, width } = this._elementRect;
    if (first) this.disconnect();

    if (down) {
      if (enableTravelling) {
        const dt = dy / height;
        t = THREE.MathUtils.clamp(t + dt, 0, 1);
      }

      if (enableRotation) {
        // rotation handling
        const isVertical = Math.abs(dy) > Math.abs(dx);
        const nearStart = this.t < 0.1;

        if (isVertical || nearStart) {
          // ease angle back toward the spline’s base angle
          const alpha = smoothFactor(timeDelta, tau);
          const targetAngle = this.baseAngleAt(this.t);
          angle = THREE.MathUtils.lerp(this.angle, targetAngle, alpha);
        } else {
          // horizontal drag rotates around tracker
          const dAngle = (dx / width) * Math.PI * 2;
          angle = this.angle + dAngle;
        }
      }

      this.setFrom(t, angle, true);
    } else {
      cancel();
    }

    if (last && this._domElement) {
      this.connect(this._domElement);
    }
  }

  get t() {
    return this._t;
  }
  get angle() {
    return this._angle;
  }
}
